import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { sdk } from "../../lib/client"
import { queryKeysFactory } from "../../lib/query-key-factory"

export type CatalogTranslationProvider = "none" | "deepl" | "google"
export type CatalogTranslationLocale = "en" | "vi"

export type CatalogTranslationFieldScopes = {
  product_title: boolean
  product_subtitle: boolean
  product_description: boolean
  category_name: boolean
}

export type CatalogTranslationRetryPolicy = {
  max_attempts: number
  retryable_errors: string[]
  non_retryable_errors: string[]
}

export type CatalogTranslationProviderHealthSummary = {
  last_success_at: string | null
  last_failure_at: string | null
  last_error_code: string | null
  last_checked_at: string | null
  snapshot_source: string | null
  status: "healthy" | "attention" | "idle"
  consecutive_failures: number
  next_retry_after: string | null
}

export type CatalogTranslationSettings = {
  id: string
  active_provider: CatalogTranslationProvider
  credentials: Record<string, string> | null
  enabled_locales: CatalogTranslationLocale[]
  auto_translate_on_create: boolean
  auto_translate_on_update: boolean
  preserve_manual_edits: boolean
  field_scopes: CatalogTranslationFieldScopes
  retry_policy: CatalogTranslationRetryPolicy
  provider_health_summary: CatalogTranslationProviderHealthSummary
}

export type CatalogTranslationSettingsResponse = {
  catalog_translation_settings: CatalogTranslationSettings
  warnings?: string[]
}

export type UpdateCatalogTranslationSettingsInput = {
  active_provider?: CatalogTranslationProvider
  enabled_locales?: CatalogTranslationLocale[]
  auto_translate_on_create?: boolean
  auto_translate_on_update?: boolean
  preserve_manual_edits?: boolean
  field_scopes?: CatalogTranslationFieldScopes
  retry_policy?: CatalogTranslationRetryPolicy
  credentials?: Record<string, string | null>
}

export type TestCatalogTranslationConnectionInput = {
  provider: Exclude<CatalogTranslationProvider, "none">
  credentials: Record<string, string>
}

export type TestCatalogTranslationConnectionResponse = {
  test_connection: {
    ok: boolean
    message?: string
    error_code?: string
  }
  provider_health_summary?: CatalogTranslationProviderHealthSummary | null
}

export type RefreshCatalogTranslationHealthResponse = {
  catalog_translation_health: CatalogTranslationProviderHealthSummary
}

export const catalogTranslationSettingsQueryKeys = queryKeysFactory(
  "catalog_translation_settings"
)

export const useCatalogTranslationSettings = () => {
  return useQuery({
    queryKey: catalogTranslationSettingsQueryKeys.all,
    queryFn: async () =>
      sdk.client.fetch<CatalogTranslationSettingsResponse>(
        "/admin/catalog-translation-settings",
        {
          method: "GET",
        }
      ),
  })
}

export const useUpdateCatalogTranslationSettings = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateCatalogTranslationSettingsInput) =>
      sdk.client.fetch<CatalogTranslationSettingsResponse>(
        "/admin/catalog-translation-settings",
        {
          method: "PUT",
          body: data,
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: catalogTranslationSettingsQueryKeys.all,
      })
    },
  })
}

export const useTestCatalogTranslationConnection = () => {
  return useMutation({
    mutationFn: async (data: TestCatalogTranslationConnectionInput) =>
      sdk.client.fetch<TestCatalogTranslationConnectionResponse>(
        "/admin/catalog-translation-settings/test-connection",
        {
          method: "POST",
          body: data,
        }
      ),
  })
}

export const useRefreshCatalogTranslationHealth = () => {
  return useMutation({
    mutationFn: async () =>
      sdk.client.fetch<RefreshCatalogTranslationHealthResponse>(
        "/admin/catalog-translation-settings?health_only=true&refresh_health=manual",
        {
          method: "GET",
        }
      ),
  })
}
